export const CALENDLY_URL = 'https://calendly.com/arushisinghaljoshi/30min'
const CALENDLY_SCRIPT_SRC = 'https://assets.calendly.com/assets/external/widget.js'
const CALENDLY_STYLESHEET_HREF = 'https://assets.calendly.com/assets/external/widget.css'

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: { url: string }) => void
    }
  }
}

let calendlyLoadPromise: Promise<void> | null = null

function ensureCalendlyStylesheet(): void {
  if (document.querySelector(`link[data-calendly="widget"]`)) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = CALENDLY_STYLESHEET_HREF
  link.setAttribute('data-calendly', 'widget')
  document.head.appendChild(link)
}

function ensureCalendlyLoaded(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.Calendly) return Promise.resolve()
  if (calendlyLoadPromise) return calendlyLoadPromise

  calendlyLoadPromise = new Promise((resolve, reject) => {
    ensureCalendlyStylesheet()
    const existingScript = document.querySelector<HTMLScriptElement>('script[data-calendly="widget"]')
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Calendly script')), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = CALENDLY_SCRIPT_SRC
    script.async = true
    script.setAttribute('data-calendly', 'widget')
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Calendly script'))
    document.body.appendChild(script)
  })

  return calendlyLoadPromise
}

/**
 * Opens the Calendly scheduling widget in a popup.
 * Falls back to opening the Calendly URL in a new tab if the widget script isn't loaded yet.
 */
export async function openCalendly(): Promise<void> {
  if (typeof window === 'undefined') return

  try {
    await ensureCalendlyLoaded()
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL })
      return
    }
  } catch {
    // Fall through to opening the fallback URL.
  }

  window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer')
}
